import React from "react";
import { Field, TextField, LongText, RadioField, CheckboxGroupField, InlineCheck, InfoPanel, SectionTitle } from "./fields";
import { MiamBlock } from "./MiamBlock";
import { Reveal, useSequentialReveal, ScrollLock } from "./reveal";
import { CASE_HELP_OPTIONS, MIAM_DETAIL_TRIGGERS } from "../../lib/options";
import { INTAKE } from "../../constants/testIds";
import { Button } from "../ui/button";
import { useBackGuard, hasAnswers } from "./useBackGuard";
import { RequiredDatesNote } from "./RequiredDatesNote";

const URGENCY = [
  { value: "today", label: "Action may be needed today." },
  { value: "few_days", label: "Action may be needed within the next few days." },
  { value: "important", label: "The issue is important but not immediately urgent." },
  { value: "gathering", label: "I am gathering information and there is no current deadline." },
  { value: "unsure", label: "I am not sure." },
];

export const RouteC = ({ form, set, config, onBack, onSubmit, submitting }) => {
  const d = form.routeC || {};
  const u = (k, v) => set("routeC", { ...d, [k]: v });
  const showUrgentDetail = d.urgency === "today" || d.urgency === "few_days";
  const { requestBack, dialog } = useBackGuard(hasAnswers(d), onBack);
  const missingDates = (showUrgentDetail && !d.urgent_date && !d.urgent_date_unknown) ? ["c-urgent-date"] : [];
  const datesOk = missingDates.length === 0;
  const otherOk = !(d.help || []).includes("Other — please state") || !!(d.help_other && d.help_other.trim());
  const miamOk = !MIAM_DETAIL_TRIGGERS.includes(d.miam) || !!d.miam_signed;

  const flags = [
    !!d.about,
    !!d.la_involved,
    !!d.urgency,
    !!d.happened,
    (d.help || []).length > 0,
    !!d.outcome || !!d.outcome_unsure,
    !!d.miam,
    !!d.accessibility || !!d.no_accessibility,
  ];
  const visible = useSequentialReveal(flags);

  return (
    <div className="space-y-8" data-testid="route-c-form">
      {dialog}
      <SectionTitle>Guidance before starting a case</SectionTitle>

      <Reveal show={visible > 0} scroll={false}>
        <Field num={1} label="What is the issue about?"><LongText value={d.about} onChange={(v) => u("about", v)} testid="c-about" /></Field>
      </Reveal>

      <Reveal show={visible > 1}>
        <Field num={2} label="Does the matter involve a local authority, social services or Cafcass?">
          <RadioField options={["Yes", "No", "I am not sure"]} value={d.la_involved} onChange={(v) => u("la_involved", v)} name="c-la" testid="c-la" />
          {d.la_involved === "Yes" && (
            <div className="mt-3">
              <RadioField
                options={[
                  "Care or supervision proceedings involving a child",
                  "Social services have had some involvement but are not a party",
                  "Another form of local-authority or Cafcass involvement",
                ]}
                value={d.la_type}
                onChange={(v) => u("la_type", v)}
                name="c-la-type"
                testid="c-la-type"
              />
              {d.la_type === "Care or supervision proceedings involving a child" && (
                <InfoPanel tone="gold" testid="c-public-scope">
                  Matters involving care or supervision proceedings may fall within public children law. We
                  will review whether this service is the appropriate route for your circumstances.
                </InfoPanel>
              )}
            </div>
          )}
        </Field>
      </Reveal>

      <Reveal show={visible > 2}>
        <Field num={3} label="How urgent is the issue?">
          <RadioField options={URGENCY} value={d.urgency} onChange={(v) => u("urgency", v)} name="c-urgency" testid="c-urgency" />
          {showUrgentDetail && (
            <div className="space-y-4 border-l-2 border-brand-gold pl-4 mt-3"><ScrollLock />
              <Field label="What needs to happen?"><LongText value={d.urgent_what} onChange={(v) => u("urgent_what", v)} testid="c-urgent-what" /></Field>
              <Field label="What is the relevant date or deadline?" required={!d.urgent_date_unknown}>
                <TextField type="date" value={d.urgent_date} onChange={(v) => u("urgent_date", v)} disabled={d.urgent_date_unknown} testid="c-urgent-date" />
                <InlineCheck checked={d.urgent_date_unknown} onChange={(v) => u("urgent_date_unknown", !!v)} label="I do not know" testid="c-urgent-date-unknown" />
              </Field>
              <Field label="Why is the matter urgent?"><LongText value={d.urgent_why} onChange={(v) => u("urgent_why", v)} testid="c-urgent-why" /></Field>
            </div>
          )}
        </Field>
      </Reveal>

      <Reveal show={visible > 3}>
        <Field num={4} label="What has happened?"><LongText value={d.happened} onChange={(v) => u("happened", v)} testid="c-happened" /></Field>
      </Reveal>

      <Reveal show={visible > 4}>
        <Field num={5} label="What help would you like?" hint="Select all that apply.">
          <CheckboxGroupField
            options={CASE_HELP_OPTIONS}
            value={d.help}
            onChange={(v) => u("help", v)}
            exclusive={["I am not sure"]}
            testid="c-help"
            extras={{
              "Other — please state": (d.help || []).includes("Other — please state") ? (
                <div>
                  <TextField value={d.help_other} onChange={(v) => u("help_other", v)} placeholder="Please state what help you need *" testid="c-help-other" />
                  {!(d.help_other && d.help_other.trim()) && <p className="text-xs text-brand-danger mt-1">Please complete this field.</p>}
                </div>
              ) : null,
            }}
          />
        </Field>
      </Reveal>

      <Reveal show={visible > 5}>
        <Field num={6} label="What outcome are you seeking?">
          <LongText value={d.outcome} onChange={(v) => u("outcome", v)} disabled={d.outcome_unsure} testid="c-outcome" />
          <InlineCheck checked={d.outcome_unsure} onChange={(v) => u("outcome_unsure", !!v)} label="I am not sure" testid="c-outcome-unsure" />
        </Field>
      </Reveal>

      <Reveal show={visible > 6}>
        <Field num={7} label="Has mediation or a MIAM (Mediation Information Assessment Meeting) been considered or attempted?">
          <MiamBlock d={d} u={u} config={config} prefix="c-miam" />
        </Field>
      </Reveal>

      <Reveal show={visible > 7}>
        <Field num={8} label="Communication or accessibility requirements">
          <LongText value={d.accessibility} onChange={(v) => u("accessibility", v)} disabled={d.no_accessibility} testid="c-accessibility" />
          <InlineCheck checked={d.no_accessibility} onChange={(v) => u("no_accessibility", !!v)} label="No additional requirements" testid="c-no-accessibility" />
        </Field>
      </Reveal>

      {visible >= flags.length && !datesOk && (
        <RequiredDatesNote missing={missingDates} />
      )}
      <div className="flex items-center justify-between pt-6 border-t border-border">
        <Button variant="ghost" onClick={requestBack} data-testid={INTAKE.back} className="text-brand-darkgrey rounded-sm">Back</Button>
        {visible >= flags.length && (
          <Button onClick={onSubmit} disabled={submitting || !datesOk || !otherOk || !miamOk} data-testid={INTAKE.submit} className="bg-brand-black text-white hover:bg-brand-gold rounded-sm px-8">
            {submitting ? "Submitting…" : "Submit enquiry"}
          </Button>
        )}
      </div>
    </div>
  );
};
